import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { FileBarChart2, RefreshCw, TrendingUp, TrendingDown, CheckCircle2, ClipboardList, Users, Wallet, Download, FileSpreadsheet, Mail } from "lucide-react";

const CURR = ["INR", "USD", "EUR", "GBP"];
const SYM = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const ROLE_LABEL = { FOUNDER: "Founder", BDOM: "BDOM", PCWM: "PCWM", GEHM: "GEHM", SMCM: "SMCM" };

function prettyMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

export default function Reports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/reports/monthly");
      setReports(data);
      if (data.length) setSelected(data[0]);
    } catch (e) {
      // Non-founder or backend down — just show empty state
      if (e?.response?.status !== 403) toast.error("Could not load reports");
    }
  };
  useEffect(() => {
    if (user.real_role === "FOUNDER") load();
  }, [user.real_role]);

  const regenerate = async (ym) => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/reports/monthly/generate${ym ? `?ym=${ym}` : ""}`);
      toast.success(`Report for ${prettyMonth(data.ym)} generated`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setRegenerating(false);
  };

  const [emailing, setEmailing] = useState(false);
  const emailReport = async (ym) => {
    const to = window.prompt("Email report to:", user.email);
    if (!to) return;
    setEmailing(true);
    try {
      const { data } = await api.post(`/reports/monthly/${ym}/email`, { recipient_email: to });
      toast.success(`Report emailed to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    }
    setEmailing(false);
  };

  if (user.real_role !== "FOUNDER") {
    return <div className="p-8 text-center text-[color:var(--aii-muted)]">Founder access only.</div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">CEO</div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><FileBarChart2 className="text-brand" /> Monthly Reports</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Auto-generated on the 1st of every month. Contains task completion, revenue, receipts/payments and C-form status.</p>
        </div>
        <button data-testid="report-regen" onClick={() => regenerate()} disabled={regenerating}
          className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw size={14} className={regenerating ? "animate-spin" : ""} /> {regenerating ? "Generating…" : "Generate now"}
        </button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-4">
        {/* Sidebar list */}
        <div className="aii-card p-2 h-fit">
          {reports.length === 0 && <div className="p-4 text-sm text-[color:var(--aii-muted)]">No reports yet. Click <span className="font-semibold">Generate now</span> to create last month&apos;s.</div>}
          {reports.map(r => (
            <button key={r.id} data-testid={`report-${r.ym}`} onClick={() => setSelected(r)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm ${selected?.ym === r.ym ? "bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-semibold" : "hover:bg-[color:var(--aii-green-tint)]"}`}>
              {prettyMonth(r.ym)}
            </button>
          ))}
        </div>

        {/* Detail */}
        {selected ? (
          <div className="space-y-4" data-testid="report-detail">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold">{prettyMonth(selected.ym)}</div>
                <div className="text-xs text-[color:var(--aii-muted)]">Period: {selected.period.start} – {selected.period.end} · Generated {selected.generated_at.slice(0,10)}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <a data-testid="report-download-xlsx" href={`${API}/reports/monthly/${selected.ym}/excel?auth=${getToken()}`}
                  className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                  <FileSpreadsheet size={11} /> Excel
                </a>
                <a data-testid="report-download-pdf" href={`${API}/reports/monthly/${selected.ym}/pdf?auth=${getToken()}`}
                  className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                  <Download size={11} /> PDF
                </a>
                <button data-testid="report-email" onClick={() => emailReport(selected.ym)} disabled={emailing}
                  className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1 disabled:opacity-50">
                  <Mail size={11} /> {emailing ? "Sending…" : "Email"}
                </button>
                <button onClick={() => regenerate(selected.ym)} disabled={regenerating} className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                  <RefreshCw size={11} /> Regenerate
                </button>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="aii-card p-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)] flex items-center gap-1"><CheckCircle2 size={12} /> Tasks completed</div>
                <div className="text-2xl font-display font-semibold text-brand mt-1">{selected.tasks.done} / {selected.tasks.total}</div>
                <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">{selected.tasks.completion_rate}% completion rate</div>
              </div>
              <div className="aii-card p-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)] flex items-center gap-1"><Users size={12} /> Arrivals</div>
                <div className="text-2xl font-display font-semibold text-brand mt-1">{selected.arrivals}</div>
                <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">participants arrived</div>
              </div>
              <div className="aii-card p-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)] flex items-center gap-1"><ClipboardList size={12} /> C-form pending</div>
                <div className={`text-2xl font-display font-semibold mt-1 ${selected.cform_overdue_now > 0 ? "text-red-700" : "text-brand"}`}>{selected.cform_overdue_now}</div>
                <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">unfiled as of now</div>
              </div>
              <div className="aii-card p-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)] flex items-center gap-1"><Wallet size={12} /> Currencies</div>
                <div className="text-2xl font-display font-semibold text-brand mt-1">{CURR.filter(c => selected.revenue[c] > 0).length}</div>
                <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">with revenue this month</div>
              </div>
            </div>

            {/* Revenue */}
            <div className="aii-card p-5">
              <h3 className="font-semibold mb-3">Revenue (bookings + weekend + activities)</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {CURR.map(c => (
                  <div key={c} data-testid={`rep-rev-${c}`}>
                    <div className="text-xs text-[color:var(--aii-muted)]">{c}</div>
                    <div className="font-display text-xl text-brand">{SYM[c]}{selected.revenue[c].toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ledger */}
            <div className="aii-card p-5">
              <h3 className="font-semibold mb-3">Ledger (Receipts &amp; Payments)</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] flex items-center gap-1 mb-2"><TrendingUp size={11} className="text-emerald-600" /> Receipts</div>
                  {CURR.map(c => (
                    <div key={c} className="flex justify-between text-sm py-1 border-b border-[color:var(--aii-border)]">
                      <span className="text-[color:var(--aii-muted)]">{c}</span>
                      <span className="font-semibold text-emerald-700">{SYM[c]}{selected.receipts[c].toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] flex items-center gap-1 mb-2"><TrendingDown size={11} className="text-red-600" /> Payments</div>
                  {CURR.map(c => (
                    <div key={c} className="flex justify-between text-sm py-1 border-b border-[color:var(--aii-border)]">
                      <span className="text-[color:var(--aii-muted)]">{c}</span>
                      <span className="font-semibold text-red-700">{SYM[c]}{selected.payments[c].toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Task performance by role */}
            <div className="aii-card p-5">
              <h3 className="font-semibold mb-3">Task completion by role</h3>
              <div className="space-y-2">
                {Object.entries(selected.tasks.by_role).map(([r, t]) => {
                  const pct = t.total ? Math.round((t.done / t.total) * 100) : 0;
                  return (
                    <div key={r} data-testid={`rep-role-${r}`}>
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{ROLE_LABEL[r] || r}</span>
                        <span className="text-[color:var(--aii-muted)]">{t.done}/{t.total} · {pct}%</span>
                      </div>
                      <div className="h-1.5 bg-[color:var(--aii-border)] rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(selected.tasks.by_role).length === 0 && (
                  <div className="text-sm text-[color:var(--aii-muted)]">No tasks logged this month.</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="aii-card p-8 text-center text-[color:var(--aii-muted)]">Select a report from the left, or click <span className="font-semibold">Generate now</span>.</div>
        )}
      </div>
    </div>
  );
}
